import { kebabCase } from 'lodash';
import { forwardRef, type ReactNode } from 'react';

import { type ResponseCustomFieldValue } from '@air/api/types';
import { Chip, type ChipProps } from '@air/primitive-chip';

import Linkify from '~/components/Linkify';
import { useExternalLinksClickableContext } from '~/providers/ExternalLinksClickableProvider';

export type CustomFieldProps = ChipProps & {
  field: Omit<ResponseCustomFieldValue, 'color'> & {
    color: Partial<Omit<ResponseCustomFieldValue['color'], 'name'>> &
      Required<Pick<ResponseCustomFieldValue['color'], 'name'>>;
  };
} & {
  suffix?: ReactNode;
};

export const CustomField = forwardRef<HTMLDivElement, CustomFieldProps>(
  ({ field, suffix, ...restOfProps }, forwardedRef) => {
    const { areExternalLinksClickable } = useExternalLinksClickableContext();

    return (
      <Chip
        color={kebabCase(field.color.name) as ChipProps['color']}
        data-testid="CUSTOM_FIELD"
        data-id={field.id}
        data-color={field.color.name}
        data-value={field.value}
        shape="rectangular"
        size="medium"
        {...restOfProps}
        ref={forwardedRef}
      >
        <span className="truncate">
          <Linkify disable={!areExternalLinksClickable}>{field.value}</Linkify>
        </span>
        {suffix}
      </Chip>
    );
  },
);

CustomField.displayName = 'CustomField';
